/**
 * Cluster GEO-DATA Fase C — NazionePicker
 *
 * Combobox shadcn (Popover + cmdk) per scegliere una nazione da
 * master_data_nazioni. Memorizza il `code` (es. "IT" per Italia oppure
 * "Z112" per Germania). La label visualizzata è il `nome`.
 *
 * Props:
 *   value         (string) codice corrente (es. "IT", "Z112") o vuoto
 *   onChange      (fn)     callback (newCode | null, nazioneObj | null)
 *   disabled      (bool)
 *   testId        (string) data-testid base
 *   placeholder   (string) testo placeholder se vuoto
 *   compact       (bool)   altezza ridotta (h-8 invece di h-10)
 *   includeItalia (bool)   se false, esclude Italia (utile per
 *                          "nazione estera di nascita" quando non IT)
 *
 * Lista in cache process-wide: ~211 record. Niente paginazione.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";

// Cache per (includeItalia=true|false)
const _cache = { true: null, false: null };
const _promise = { true: null, false: null };

async function loadNazioni(includeItalia = true) {
  const key = includeItalia ? "true" : "false";
  if (_cache[key]) return _cache[key];
  if (_promise[key]) return _promise[key];
  _promise[key] = api.get(`/master-data/nazioni?include_italia=${includeItalia}`)
    .then((r) => {
      _cache[key] = (r.data && r.data.items) || [];
      _promise[key] = null;
      return _cache[key];
    })
    .catch(() => {
      _promise[key] = null;
      return [];
    });
  return _promise[key];
}

export function invalidateNazioniCache() {
  _cache.true = null; _cache.false = null;
  _promise.true = null; _promise.false = null;
}

export default function NazionePicker({
  value, onChange, disabled, testId,
  placeholder = "Nazione…", compact = false, includeItalia = true,
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let m = true;
    setLoading(true);
    loadNazioni(includeItalia).then((d) => {
      if (m) { setList(d); setLoading(false); }
    });
    return () => { m = false; };
  }, [includeItalia]);

  const code = (value || "").toString().toUpperCase();
  const selected = useMemo(
    () => list.find((n) => (n.code || "").toUpperCase() === code) || null,
    [list, code],
  );

  const heightCls = compact ? "h-8 text-xs px-2" : "h-10 text-sm px-3";

  const triggerLabel = selected
    ? <span>{selected.nome}<span className="text-slate-400 ml-1.5 font-mono text-xs">{selected.code}</span></span>
    : value
      ? <span className="font-mono">{code}</span>
      : <span className="text-slate-400">{loading ? "…" : placeholder}</span>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || loading}
            data-testid={testId}
            className={cn(
              "w-full justify-between font-normal bg-white",
              heightCls,
              value && !disabled ? "pr-12" : "",
            )}
          >
            <span className="truncate text-left flex-1">{triggerLabel}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 ml-1 shrink-0" />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange?.(null, null);
            }}
            data-testid={testId ? `${testId}-clear` : undefined}
            title="Rimuovi selezione"
            className="absolute right-7 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-rose-600 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
        <Command
          filter={(itemValue, search) => {
            const n = list.find((x) => x.code === itemValue);
            if (!n) return 0;
            const hay = `${n.code} ${n.nome || ""} ${n.iso || ""}`.toLowerCase();
            return hay.includes((search || "").toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca nome o codice ISO…"
                        data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>Nessuna nazione trovata.</CommandEmpty>
            <CommandGroup>
              {list.map((n) => (
                <CommandItem
                  key={n.code}
                  value={n.code}
                  onSelect={(v) => {
                    const newV = (v || "").toUpperCase();
                    if (newV === code) {
                      onChange?.(null, null);
                    } else {
                      onChange?.(newV, n);
                    }
                    setOpen(false);
                  }}
                  data-testid={testId ? `${testId}-opt-${n.code}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4",
                    code === n.code ? "opacity-100" : "opacity-0")} />
                  <div className="flex items-baseline gap-2 flex-1 min-w-0">
                    <span className="text-sm">{n.nome}</span>
                    <span className="font-mono text-[10px] text-slate-400">{n.code}</span>
                    {n.iso && (
                      <span className="text-slate-400 text-xs">— {n.iso}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
